<template>
 <ul class="flex flex-col w-full max-w-[800px] gap-2">
  <li
    v-for="detail in detailList"
    :key="detail"
    class="flex justify-start w-full gap-5"
    :class="{ 'mt-3': detail.type === 'textarea' }"
  >
   <strong
     class="text-sm leading-7 font-semibold -tracking-[0.2px] w-20 min-w-20"
     :class="{ 'self-start': detail.type === 'textarea' }"
   >
    {{ detail.title }}
   </strong>
   <div
     v-if="detail.type === 'text'"
     class="inline-flex flex-1 text-sm font-semibold leading-7 border-b border-b-gray-300 text-gray-600"
   >
    {{ detailData[detail.value] }}
   </div>
   <Table
     v-else-if="detail.type === 'table'"
     v-bind="detail.tableOptions"
     :rows="detailData[detail.value]"
   >
     <template v-if="detail.value === 'crawlStatus'" #td-status="{ row, col }">
       <div class="flex items-center justify-center gap-2">
         <a 
           v-if="row.status === '완료' && row.lucy_path"
           @click.prevent="downloadLucyFile(row.result_cvs_path)"
           href="#"
           class="text-violet-500 underline hover:text-violet-700 cursor-pointer"
           :class="{ 'opacity-50 pointer-events-none': downloadingFile }"
         >
           {{ downloadingFile ? '준비 중...' : row.status }}
         </a>
         <span v-else>{{ row.status }}</span>
       </div>
     </template>
   </Table>
   <div
     v-if="detail.type === 'file'"
     class="inline-flex flex-1 gap-2 text-sm font-semibold leading-7 text-gray-05 flex-nowrap"
   >
    <FileInput
      v-bind="detail.inputOptions"
      :value="detailData[detail.value]"
      class="cursor-pointer"
      @update:model-value="(file) => {
       detailData[detail.value] = file
      }"
    />
    <button
      class="w-24 px-3 text-white rounded-5 bg-slate-800 hover:bg-slate-900 transition-colors"
      @click="startCrawling"
    >
     <div
       class="w-5 h-5 mx-auto border-4 border-white rounded-full border-t-transparent animate-spin"
       v-if="crawlingLoading"
     ></div>
     <template v-else>크롤링 시작</template>
    </button>
   </div>
   <div
     v-if="detail.type === 'textarea'"
     class="inline-flex flex-1 gap-2 text-sm flex-col"
   >
    <textarea
      v-model="detailData[detail.value]"
      v-bind="detail.inputOptions"
      class="w-full px-3 py-2 text-sm border border-gray-300 rounded-5 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 resize-none"
    />
    <button
      v-if="detail.showButton !== false"
      class="w-24 px-3 py-2 text-white rounded-5 bg-slate-800 hover:bg-slate-900 transition-colors self-end"
      @click="savePrompt"
    >
     <div
       class="w-5 h-5 mx-auto border-4 border-white rounded-full border-t-transparent animate-spin"
       v-if="promptSaving"
     ></div>
     <template v-else>저장</template>
    </button>
   </div>
  </li>
 </ul>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { useStore } from 'vuex';
import Table from '@/components/ui/Table.vue'
import FileInput from '@/components/ui/FileInput.vue';

const detailList = [
 {
  title: '프로젝트 명',
  type: 'text',
  value: 'name'
 },
 {
  title: '프로젝트 기간',
  type: 'text',
  value: 'projectRange'
 },
 {
  title: '투입 인원',
  type: 'table',
  value: 'workers',
  tableOptions: {
   class: 'w-full',
   columns: [
    { label: 'No', value: 'idx' },
    { label: '소속', value: 'department' },
    { label: '직급', value: 'position', headerClass: 'whitespace-nowrap' },
    { label: '성명', value: 'name' },
    { label: '서비스 지원분야', value: 'serviceArea', headerClass: 'whitespace-nowrap w-[20%]' },
    { label: '프로젝트 역할', value: 'role', headerClass: 'whitespace-nowrap w-[20%]' },
   ],
  }
 },
 {
  title: 'LUCY 파일',
  type: 'file',
  value: 'lucyFile',
  inputOptions: {
   placeholder: "파일 업로드",
   class: "flex-1",
   type: 'file',
   accept: '.xls,.xlsx,.csv'
  }
 },
 {
  title: '크롤링 현황',
  type: 'table',
  value: 'crawlStatus',
  tableOptions: {
   class: 'w-full',
   columns: [
    { label: '요청시간', value: 'range', headerClass: '!bg-gray-02 !text-black w-[30%] text-center' },
    { label: 'Lucy 파일', value: 'fileName', headerClass: '!bg-gray-02 !text-black text-center w-[30%] truncate' },
    { label: '상태', value: 'status', headerClass: '!bg-gray-02 !text-black min-w-24 text-center w-[15%]' },
    { label: '진행상황', value: 'progress', headerClass: '!bg-gray-02 !text-black min-w-24 text-center w-[25%]' },
   ],
  }
 },
 {
  title: '실시간 크롤링 현황',
  type: 'table',
  value: 'realtimeCrawlStatus',
  tableOptions: {
   class: 'w-full',
   tableClass: 'max-h-[250px]',
   columns: [
    { label: '채널', value: 'channels', headerClass: '!bg-gray-02 !text-black w-[25%] text-center' },
    // { label: '모드', value: 'mode', headerClass: '!bg-gray-02 !text-black text-center w-[15%]' },
    { label: '키워드', value: 'keywords', headerClass: '!bg-gray-02 !text-black text-center w-[15%]' },
    { label: '시작시간', value: 'startTime', headerClass: '!bg-gray-02 !text-black text-center w-[20%] whitespace-nowrap' },
    { label: '종료시간', value: 'endTime', headerClass: '!bg-gray-02 !text-black text-center w-[20%] whitespace-nowrap' },
    // { label: '상태', value: 'status', headerClass: '!bg-gray-02 !text-black min-w-24 text-center w-[10%]' },
    // { label: '진행상황', value: 'progress', headerClass: '!bg-gray-02 !text-black min-w-24 text-center w-[10%] whitespace-nowrap' },
   ],
  }
 },
 {
  title: '단순 프롬프트',
  type: 'textarea',
  value: 'simplePrompt',
  showButton: false,
  inputOptions: {
   placeholder: "프롬프트를 입력하세요",
   rows: 4
  }
 },
 {
  title: '단순 프롬프트 예시',
  type: 'textarea',
  value: 'simplePromptExample',
  inputOptions: {
   placeholder: "프롬프트 예시를 입력하세요",
   rows: 4
  }
 },
]

const props = defineProps({
 modelValue: {
  type: [Object, null],
  default: () => ({
   name: '',
   range: '',
   workers: [],
   LUCYfile: null,
  }),
 }
})

const emits = defineEmits(['update:model-value'])
const store = useStore()
const downloadingFile = computed(() => store.state.file?.downloadingFile);

const statusMap = {
 'STARTED': '진행중',
 'PROCESSING': '진행중',
 'COMPLETED': '완료',
 'INITIALIZED': '오류',
 'pending': '대기중',
 'CREATED': '생성됨'
}

const detailData = ref({})

const S3_BASE_URL = 'https://prone-llm.s3.ap-northeast-2.amazonaws.com';

const generateS3Url = (path) => {
  if (!path || path === 'none') return '';
  return `${S3_BASE_URL}/${path}`;
};

const downloadLucyFile = async (path) => {
  if (!path || path === 'none') return;
  
  try {
    const success = await store.dispatch('file/downloadFile', path);
    
    if (!success) {
      store.commit('handleNotify', { 
        message: '파일 다운로드에 실패했습니다. 다시 시도해주세요.', 
        type: 'error' 
      });
    }
  } catch (error) {
    console.error('다운로드 오류:', error);
    store.commit('handleNotify', { 
      message: error.response?.data?.detail || '파일 다운로드 중 오류가 발생했습니다.', 
      type: 'error' 
    });
  }
};

onMounted(async () => {
 const formatDate = (date) => {
   return date.toLocaleString('ko-KR', {
     year: 'numeric',
     month: '2-digit',
     day: '2-digit',
     hour: '2-digit',
     minute: '2-digit',
     hour12: false
   }).replace(/\./g, '-')
 }

 // 프로젝트 정보 조회
 let projectInfo = null
 try {
   projectInfo = await store.dispatch('project/fetchProjectInfo', props.modelValue.id)
 } catch (error) {
   console.error('Failed to fetch project info:', error)
   // 실패 시 기존 props 데이터 사용
   projectInfo = {
     simple_mention_prompt: props.modelValue.simple_mention_prompt || ''
   }
 }

 detailData.value = {
  ...props.modelValue,
  lucyFile: null,
  crawlStatus: props.modelValue.crawling_jobs
    .filter(job => job.job_type !== 'realtime')  // 일반 크롤링 작업만 필터링
    .map((job) => ({
      ...job,
      fileName: job.lucy_path && job.lucy_path !== 'none'
        ? job.lucy_path.split('/').pop()
        : '-',
      range: formatDate(new Date(job.started_at)),
      status: statusMap?.[job.status] || job.status,
      progress: job.total_items ? `${job.processed_items} / ${job.total_items}` : '-',
    })),
  realtimeCrawlStatus: props.modelValue.crawling_jobs
    .filter(job => job.job_type === 'realtime')  // 실시간 크롤링 작업만 필터링
    .map((job) => ({
      ...job,
      channels: Array.isArray(job.channels) ? job.channels.join(', ') : (job.channels || '-'),
      mode: job.mode || '-',
      startTime: job.crawl_start_date ? formatDate(new Date(job.crawl_start_date)) : '-',
      endTime: job.crawl_end_date ? formatDate(new Date(job.crawl_end_date)) : '-',
      status: statusMap?.[job.status] || job.status,
      progress: job.total_items ? `${job.processed_items} / ${job.total_items}` : '-',
    })),
  simplePrompt: projectInfo.simple_mention_prompt || '',
  simplePromptExample: projectInfo.simple_mention_example || '',
  workers: [],
  projectRange: `${props.modelValue.start_date} ~ ${props.modelValue.end_date}`,
 }
 console.log('서버에서 조회된 데이터:', props.modelValue.crawling_jobs)
 console.log('그리드에 세팅하기 위한 데이터:', detailData.value.crawlStatus)
 console.log('조회된 단순 프롬프트:', projectInfo.simple_mention_prompt)

 async function loadParticipants() {
  if (!props.modelValue.participant_ids.length) {
   return
  }

  try {
   const participants = await store.dispatch('project/fetchParticipantsByIds', props.modelValue.participant_ids)
   detailData.value.workers = participants.map((participant, idx) => ({ ...participant, idx: idx + 1 }));
  } catch (error) {
   console.error('Failed to load participants:', error);
  }
 }
 loadParticipants()

})


const crawlingLoading = ref(false)
async function startCrawling() {
 if (!detailData.value.lucyFile?.name) {
  store.commit('handleNotify', { message: '파일을 등록해주세요.' })
  alert('파일을 등록해주세요.')
  return
 }

 try {
  const maxSizeInMB = 100;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024; // MB to Bytes 변환

  if (detailData.value.lucyFile.size > maxSizeInBytes) {
    alert(`파일 용량은 최대 ${maxSizeInMB}MB까지 업로드할 수 있습니다.`);
    return;
  }

  crawlingLoading.value = true;
  await store.dispatch('crawling/startCrawling', {
   file: detailData.value.lucyFile,
   selected_project_id: props.modelValue.id
  })

  store.commit('handleNotify', { message: '크롤링 등록에 성공하였습니다.', type: 'success' })
  const errMsg = store.state.notify.message
  alert(errMsg)
 } catch (error) {
  store.commit('handleNotify', { message: '오류가 발생했습니다.' })

  const errMessage = error.response.data.message
  alert(errMessage)
 } finally {
  crawlingLoading.value = false
 }
}

const promptSaving = ref(false)
async function savePrompt() {
 if (!detailData.value.simplePrompt?.trim()) {
  store.commit('handleNotify', { message: '프롬프트를 입력해주세요.' })
  alert('프롬프트를 입력해주세요.')
  return
 }

 try {
  promptSaving.value = true

  await store.dispatch('project/updateSimpleMentionPrompt', {
    project_id: props.modelValue.id,
    simple_mention_prompt: detailData.value.simplePrompt,
    simple_mention_example: detailData.value.simplePromptExample
  })

  // 저장 성공 후 최신 데이터 조회하여 반영
  try {
    const projectInfo = await store.dispatch('project/fetchProjectInfo', props.modelValue.id)
    detailData.value.simplePrompt = projectInfo.simple_mention_prompt || ''
    detailData.value.simplePromptExample = projectInfo.simple_mention_example || ''
    console.log('저장 후 업데이트된 프롬프트:', projectInfo.simple_mention_prompt)
    console.log('저장 후 업데이트된 프롬프트 예시:', projectInfo.simple_mention_example)
  } catch (fetchError) {
    console.error('Failed to refresh project info:', fetchError)
    // 조회 실패해도 저장은 성공했으므로 계속 진행
  }

  store.commit('handleNotify', { message: '프롬프트가 저장되었습니다.', type: 'success' })
  alert('프롬프트가 저장되었습니다.')

 } catch (error) {
  store.commit('handleNotify', { message: '저장 중 오류가 발생했습니다.' })
  const errMessage = error.response?.data?.detail || error.response?.data?.message || '저장 실패'
  alert(errMessage)
 } finally {
  promptSaving.value = false
 }
}

</script>

<style lang="scss" scoped></style>
